import React, { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from './ui/table';
import { Factory } from 'lucide-react';
import { DailyMetalData } from '../types/metal';
import { formatNumberDisplay } from '@/lib/utils';

interface MetalTableProps {
  dailyData: DailyMetalData[];
  isLoading: boolean;
  error: string | null;
  processes: string[];
  title: string;
}

const MetalTable: React.FC<MetalTableProps> = ({ 
  dailyData, 
  isLoading, 
  error, 
  processes, 
  title 
}) => {
  // Process configuration
  const processConfig = {
    phoi: { name: 'Phôi', shortName: 'Phôi', color: 'bg-amber-50' },
    han: { name: 'Hàn', shortName: 'Hàn', color: 'bg-red-50' },
    son: { name: 'Sơn', shortName: 'Sơn', color: 'bg-blue-50' },
    kiemTran: { name: 'Kiểm trần', shortName: 'K.Trần', color: 'bg-violet-50' },
    kiemDan: { name: 'Kiểm đan', shortName: 'K.Đan', color: 'bg-emerald-50' },
    thanhPham: { name: 'Thành phẩm', shortName: 'T.Phẩm', color: 'bg-orange-50' },
  };

  // Sort data by date (newest first)
  const sortedData = useMemo(() => {
    return [...dailyData].sort((a, b) => 
      new Date(b.date).getTime() - new Date(a.date).getTime()
    );
  }, [dailyData]);

  // Format numbers
  // Use Vietnamese locale formatting for numbers

  // Format date
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return `${date.getDate().toString().padStart(2, '0')}/${(date.getMonth() + 1).toString().padStart(2, '0')}`;
  };

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Factory className="h-5 w-5" />
            {title}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <p className="text-red-600">Lỗi tải dữ liệu: {error}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Factory className="h-5 w-5" />
          {title}
        </CardTitle>
        <CardDescription>
          Công lao động, trọng lượng và năng suất theo từng ngày và công đoạn
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-12 bg-gray-200 animate-pulse rounded"></div>
            ))}
          </div>
        ) : sortedData.length > 0 ? (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                {/* First Row: Process Names (Merged) */}
                <TableRow>
                  <TableHead rowSpan={2} className="text-center border-r w-[100px] align-middle">
                    Ngày
                  </TableHead>
                  {processes.map((processKey) => {
                    const config = processConfig[processKey as keyof typeof processConfig];
                    return (
                      <TableHead 
                        key={processKey}
                        colSpan={3} 
                        className={`text-center border-r font-semibold ${config.color}`}
                      >
                        {config.name}
                      </TableHead>
                    );
                  })}
                </TableRow>
                
                {/* Second Row: Sub-columns */}
                <TableRow>
                  {processes.map((processKey) => (
                    <React.Fragment key={processKey}>
                      <TableHead className="text-center border-r w-[80px]">Công</TableHead>
                      <TableHead className="text-center border-r w-[80px]">Kg</TableHead>
                      <TableHead className="text-center border-r w-[80px]">NS</TableHead>
                    </React.Fragment>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedData.map((day, index) => (
                  <TableRow key={day.date} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                    <TableCell className="font-medium text-center border-r">
                      {formatDate(day.date)}
                    </TableCell>
                    
                    {processes.map((processKey) => {
                      const processData = day.processes[processKey as keyof typeof day.processes];
                      return (
                        <React.Fragment key={processKey}>
                          <TableCell className="text-center border-r">
                            {formatNumberDisplay(processData.labor || 0, 0)}
                          </TableCell>
                          <TableCell className="text-center border-r">
                            {formatNumberDisplay(processData.weight || 0, 2)}
                          </TableCell>
                          <TableCell className="text-center border-r">
                            {formatNumberDisplay(processData.productivity || 0, 2)}
                          </TableCell>
                        </React.Fragment>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <div className="text-center py-8">
            <p className="text-gray-500">Không có dữ liệu</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default MetalTable;